import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createDefaultPlatformConfig,
  DEFAULT_SESSION_IDLE_MINUTES,
  normalizePlatformConfig,
  sessionIdleMinutesToMs,
  type PlatformConfig,
} from '../core/models/PlatformConfig';
import { firebaseEmulatorsEnabled } from '../config/env';
import { defaultDevPanelFlag } from '../config/devPanelAccess';
import { useData } from './DataContext';
import { useDataSync } from '../hooks/useDataSync';

const SESSION_IDLE_TIMEOUT_MS = DEFAULT_SESSION_IDLE_MINUTES * 60 * 1000;

interface PlatformConfigContextValue {
  config: PlatformConfig;
  sessionIdleMs: number;
  updatePlatformConfig: (patch: Partial<PlatformConfig>) => Promise<void>;
}

const PlatformConfigCtx = createContext<PlatformConfigContextValue | null>(null);

export function PlatformConfigProvider({ children }: { children: ReactNode }) {
  const { adapter } = useData();
  const remote = useDataSync(adapter, 'platformConfig', []);
  const emulatorMode = firebaseEmulatorsEnabled();
  const [config, setConfig] = useState<PlatformConfig>(() =>
    createDefaultPlatformConfig(emulatorMode, { devPanel: defaultDevPanelFlag() }),
  );

  useEffect(() => {
    const row = Array.isArray(remote) ? remote[0] : remote;
    if (row) {
      setConfig(normalizePlatformConfig(row, emulatorMode));
    }
  }, [remote, emulatorMode]);

  const updatePlatformConfig = useCallback(async (patch: Partial<PlatformConfig>) => {
    if (adapter.updatePlatformConfig) {
      await adapter.updatePlatformConfig(patch);
    }
    setConfig((prev) => normalizePlatformConfig({
      ...prev,
      ...patch,
      featureFlags: { ...prev.featureFlags, ...patch.featureFlags },
      authProviders: { ...prev.authProviders, ...patch.authProviders },
      mfaMethods: { ...prev.mfaMethods, ...patch.mfaMethods },
    }, emulatorMode));
  }, [adapter, emulatorMode]);

  const value = useMemo(
    () => ({
      config,
      sessionIdleMs: sessionIdleMinutesToMs(config.sessionIdleMinutes) || SESSION_IDLE_TIMEOUT_MS,
      updatePlatformConfig,
    }),
    [config, updatePlatformConfig],
  );

  return (
    <PlatformConfigCtx.Provider value={value}>
      {children}
    </PlatformConfigCtx.Provider>
  );
}

export function usePlatformConfig() {
  const ctx = useContext(PlatformConfigCtx);
  if (!ctx) throw new Error('usePlatformConfig must be used within PlatformConfigProvider');
  return ctx;
}
